import { Button, ConfirmModal, Input } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { type ReactNode, useCallback, useState } from 'react';

import * as styles from './settings-typed-confirm.css';

export interface SettingsTypedConfirmButtonProps {
  /** Label of the button in the row and of the confirming button. */
  label: string;
  /** Shown on the confirming button while the action runs. */
  busyLabel: string;
  title: string;
  description: ReactNode;
  /** The word the viewer has to type before the action can run. */
  word: string;
  /** Resolves to true once the action went through; the dialog then closes. */
  onConfirm: () => Promise<boolean>;
  disabled?: boolean;
  testId?: string;
  inputTestId?: string;
}

/**
 * A destructive action that affects everyone in the workspace: it runs only
 * after the viewer typed its confirmation word.
 */
export const SettingsTypedConfirmButton = ({
  label,
  busyLabel,
  title,
  description,
  word,
  onConfirm,
  disabled,
  testId,
  inputTestId,
}: SettingsTypedConfirmButtonProps) => {
  const t = useI18n();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);

  const changeOpen = useCallback((next: boolean) => {
    setOpen(next);
    if (!next) setTyped('');
  }, []);
  const confirm = useCallback(() => {
    setBusy(true);
    onConfirm()
      .then(done => {
        if (done) changeOpen(false);
      })
      .catch(error => console.error(error))
      .finally(() => setBusy(false));
  }, [changeOpen, onConfirm]);

  return (
    <>
      <Button
        variant="error"
        disabled={disabled || busy}
        onClick={() => setOpen(true)}
        data-testid={testId}
      >
        {busy ? busyLabel : label}
      </Button>
      <ConfirmModal
        open={open}
        onOpenChange={changeOpen}
        title={title}
        description={description}
        confirmText={busy ? busyLabel : label}
        cancelText={t['Cancel']()}
        confirmButtonOptions={{
          variant: 'error',
          disabled: typed.trim() !== word || busy,
          loading: busy,
        }}
        onConfirm={confirm}
      >
        <div className={styles.prompt}>
          {t['com.streamlain.settings.ui.confirm-word']()}{' '}
          <span className={styles.word}>{word}</span>
        </div>
        <Input
          type="text"
          value={typed}
          onChange={setTyped}
          placeholder={word}
          data-testid={inputTestId}
        />
      </ConfirmModal>
    </>
  );
};
